import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  LegacyAuthResponse,
  LegacyRefreshTokenResponse,
  CognitoAuthData,
  CognitoRefreshTokenResponse,
  CognitoPermission,
} from '../../../services/auth/types';

export interface AuthState {
  isActive: boolean;
  isFullRegister: boolean;
  msg: string | null;
  // Legacy auth fields
  token?: string | null;
  refresh?: string | null;
  // Cognito auth fields
  idToken?: string | null;
  refreshToken?: string | null;
  accessToken?: string | null;
  userKyc: boolean;
  isEmailConfirmed?: boolean | undefined;
  has2FA?: boolean | undefined;
  roles?: string[];
  permissions?: CognitoPermission[];
  hydrationCompleted: boolean;
}

const initialState: AuthState = {
  isActive: false,
  isFullRegister: false,
  msg: null,
  token: null,
  refresh: null,
  idToken: null,
  refreshToken: null,
  accessToken: null,
  userKyc: false,
  isEmailConfirmed: undefined,
  roles: [],
  permissions: [],
  hydrationCompleted: false,
};

export const authState = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    authenticate: (state, action: PayloadAction<LegacyAuthResponse>) => {
      state.isFullRegister = action.payload.isFullRegister;
      state.isActive = action.payload.isActive;
      state.msg = action.payload.msg;

      // Clear Cognito fields
      state.idToken = null;
      state.refreshToken = null;
      state.accessToken = null;

      // Set legacy fields
      state.token = action.payload.token;
      state.refresh = action.payload.refresh;
      state.userKyc = action.payload.user_kyc;
      state.isEmailConfirmed = action.payload.is_email_confirmed;
      state.roles = [];
      state.permissions = [];

      if (import.meta.env.VITE_REACT_APP_ENV === 'local') {
        state.isEmailConfirmed = true;
      }
    },

    cognitoAuthenticate: (state, action: PayloadAction<CognitoAuthData>) => {
      state.isFullRegister = false;
      state.isActive = false;
      state.msg = null;

      // Clear legacy fields
      state.token = null;
      state.refresh = null;

      // Set Cognito-specific fields
      state.idToken = action.payload.idToken;
      state.refreshToken = action.payload.refreshToken;
      state.accessToken = action.payload.accessToken;
      state.permissions = action.payload.permissions || [];
      state.roles = action.payload.roles || [];
      state.userKyc = false;
      state.isEmailConfirmed = false;

      if (import.meta.env.VITE_REACT_APP_ENV === 'local') {
        state.isEmailConfirmed = true;
      }
    },

    logout: (state) => {
      Object.assign(state, {
        ...initialState,
        hydrationCompleted: true,
      });
    },

    legacyTokenRefreshed: (
      state,
      action: PayloadAction<LegacyRefreshTokenResponse>
    ) => {
      state.token = action.payload.token;
    },

    cognitoTokenRefreshed: (
      state,
      action: PayloadAction<CognitoRefreshTokenResponse>
    ) => {
      state.idToken = action.payload.idToken;
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
    },

    setHydrationCompleted: (state, action: PayloadAction<boolean>) => {
      state.hydrationCompleted = action.payload;
    },
  },
});

export const {
  authenticate,
  cognitoAuthenticate,
  logout,
  legacyTokenRefreshed,
  cognitoTokenRefreshed,
  setHydrationCompleted,
} = authState.actions;

export default authState.reducer;
